import { startOfDay, endOfDay, isSameDay } from 'date-fns';
import { SHEET_EVENT } from 'subscriptions/events/events.enum';
import { subscriptionsFabric } from 'subscriptions/subscriptions.fabric';
import workerSchedulePageActions from 'store/workerSchedule/workerSchedulePageActions';
import { fetchMyScheduleSheets } from 'store/workerSchedule/workerSchedulePageOperations';
import { fetchPersonalProfile } from 'store/personalProfile/personalProfileOperations';
import { getSheetResourceDictionaries, getSheetResourceUpdates } from 'subscriptions/utils';

const refetchMySchedule = (store) =>
  store.dispatch(fetchMyScheduleSheets(store.getState().workerSchedule.mySchedule.selectedDate));

const handlers = {
  [SHEET_EVENT.myScheduleAdded]: async (store) => {
    let userId = store.getState().personalProfile?.user?._id;
    if (!userId) await store.dispatch(fetchPersonalProfile());

    userId = store.getState().personalProfile?.user?._id;
    
    const workerSheetEvent = SHEET_EVENT.myScheduleAdded.replace('{userId}', userId);
    
    const listener = (data) => {
      const { selectedDate, sheets } = store.getState().workerSchedule.mySchedule;
      const sheetInSchedule = sheets.some((sheet) => sheet._id === data?._id);

      if (data.published && !sheetInSchedule && isSameDay(new Date(data.createdAt), selectedDate)) {
        store.dispatch(workerSchedulePageActions.addMyScheduleSheet(data));
      }
    };

    return { name: workerSheetEvent, listener };
  },
  [SHEET_EVENT.updated]: (store) => (data) => {
    const { selectedDate, sheets } = store.getState().workerSchedule.mySchedule;
    if (!sheets.some((sheet) => sheet._id === data?._id)) return;

    const action = isSameDay(new Date(data.createdAt), selectedDate)
      ? workerSchedulePageActions.putMyScheduleSheet
      : workerSchedulePageActions.deleteMyScheduleSheet;
    store.dispatch(action(data));
  },
  [SHEET_EVENT.resourcesAvailability]: (store) => async (data) => {
    let userId = store.getState().personalProfile?.user?._id;
    if (!userId) await store.dispatch(fetchPersonalProfile());

    userId = store.getState().personalProfile?.user?._id;
    const sheets = store.getState().workerSchedule.mySchedule.sheets;
    const date = store.getState().workerSchedule.mySchedule.selectedDate;

    if (
      new Date(data.from)?.getTime() > endOfDay(date).getTime() ||
      new Date(data.to)?.getTime() < startOfDay(date).getTime()
    ) return;

    const { updatedWorkersBySheet, updatedEquipmentBySheet } = getSheetResourceDictionaries(
      sheets,
      data.workers,
      data.equipment
    );

    for (const sheet of sheets) {
      const sheetUpdatedWorkers = updatedWorkersBySheet[sheet._id];
      const sheetUpdatedEquipment = updatedEquipmentBySheet[sheet._id];
      if (!sheetUpdatedWorkers?.workers?.length && !sheetUpdatedEquipment?.equipment?.length) continue;
      
      const { workers, equipment } = getSheetResourceUpdates(sheet, sheetUpdatedWorkers?.workers, sheetUpdatedEquipment?.equipment);

      if (!workers.some(w => w._id === userId) && sheet.owner !== userId) {
        store.dispatch(workerSchedulePageActions.deleteMyScheduleSheet(sheet));
        continue;
      }

      store.dispatch(workerSchedulePageActions.patchMyScheduleSheet({
        ...sheet,
        workers,
        equipment,
      }));
    }
  },
  [SHEET_EVENT.deleted]: (store) => (data) => {
    const sheets = store.getState().workerSchedule.mySchedule.sheets;
    const sheetInSchedule = sheets.some((sheet) => sheet._id === data?._id);

    if (sheetInSchedule) store.dispatch(workerSchedulePageActions.deleteMyScheduleSheet(data));
  },
  [SHEET_EVENT.notes]: (store) => (data) => {
    const sheets = store.getState().workerSchedule.mySchedule.sheets;
    const sheetInSchedule = sheets.some((sheet) => sheet._id === data?._id);

    if (sheetInSchedule) {
      store.dispatch(workerSchedulePageActions.patchMyScheduleSheet(data));
    }
  },
  [SHEET_EVENT.updatedCrewLeader]: (store) => (data) => {
    const sheets = store.getState().workerSchedule.mySchedule.sheets;
    const sheetInSchedule = sheets.some((sheet) => sheet._id === data?._id);

    if (sheetInSchedule) store.dispatch(workerSchedulePageActions.patchMyScheduleSheetWorker(data));
  },
  [SHEET_EVENT.canceled]: (store) => (data) => {
    const sheets = store.getState().workerSchedule.mySchedule.sheets;
    const sheetInSchedule = sheets.some((sheet) => sheet._id === data?._id);

    if (sheetInSchedule) store.dispatch(workerSchedulePageActions.patchMyScheduleSheet(data));
  },
  [SHEET_EVENT.uncanceled]: (store) => (data) => {
    const sheets = store.getState().workerSchedule.mySchedule.sheets;
    const sheetInSchedule = sheets.some((sheet) => sheet._id === data?._id);

    if (sheetInSchedule)
      store.dispatch(
        workerSchedulePageActions.patchMyScheduleSheet({
          ...data,
          canceledAt: null,
          canceledBy: null,
        })
      );
  },
  [SHEET_EVENT.publishedBunch]: (store) => () => refetchMySchedule(store),
  [SHEET_EVENT.submitted]: (store) => (data) => {
    const sheets = store.getState().workerSchedule.mySchedule.sheets;
    const sheetInSchedule = sheets.some((sheet) => sheet._id === data?._id);

    if (sheetInSchedule) {
      const submissions = sheetInSchedule.submissions?.map((sub) => sub) || [];

      if (data.submission && !submissions?.some(sub => sub._id === data?.submission?._id)) {
        submissions.push(data.submission);
      }

      const updates = {
        _id: data._id,
        notes: data.notes || sheetInSchedule.notes || '',
        submittedAt: data.submittedAt,
        submittedBy: data.submittedBy,
        submissions: submissions,
      };

      store.dispatch(workerSchedulePageActions.patchMyScheduleSheet(updates));
    }
  },
  [SHEET_EVENT.submissionDeleted]: (store) => (data) => {
    const sheets = store.getState().workerSchedule.mySchedule.sheets;
    const sheetInSchedule = sheets.some((sheet) => sheet._id === data?._id);

    if (sheetInSchedule) {
      let submissions = sheetInSchedule.submissions;
      if (submissions?.length)
        submissions = submissions.filter((submission) => submission._id !== data.submission?._id);

      const updates = {
        _id: data._id,
        notes: data.notes || sheetInSchedule.notes || '',
        submittedAt: data.submittedAt,
        submittedBy: data.submittedBy,
      };
      if (submissions) updates.submissions = submissions.map((sub) => sub);

      store.dispatch(workerSchedulePageActions.patchMyScheduleSheet(updates));
    }
  },
};

export const myScheduleSheetsSubscriptionMiddleware = subscriptionsFabric(
  workerSchedulePageActions,
  SHEET_EVENT,
  handlers,
  { assignedToLocations: () => true, onReconnect: refetchMySchedule }
);
